import React, { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import moment from 'moment';
import { DocumentChartBarIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import logger from '../../utils/logger';

const TaskCompletionReport = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    const fetchTasks = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'tasks'));
        const tasksData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setTasks(tasksData);
        setFilteredTasks(tasksData);
      } catch (error) {
        logger.error('Error fetching tasks:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTasks();
  }, []);

  useEffect(() => {
    if (startDate && endDate) {
      const start = moment(startDate).startOf('day');
      const end = moment(endDate).endOf('day');
      const filtered = tasks.filter(task => {
        if (!task.completedAt) return false;
        const completedDate = moment(task.completedAt.toDate());
        return completedDate.isBetween(start, end, null, '[]');
      });
      setFilteredTasks(filtered);
    } else {
      setFilteredTasks(tasks);
    }
  }, [startDate, endDate, tasks]);

  const calculateTimeDifference = (start, end) => {
    if (!start || !end) return 'N/A';
    const duration = moment.duration(moment(end.toDate()).diff(moment(start.toDate())));
    const hours = Math.floor(duration.asHours());
    const minutes = duration.minutes();
    return `${hours}h ${minutes}m`;
  };

  const checkOnTime = (deadline, completedAt) => {
    if (!deadline || !completedAt) return { status: 'N/A', class: 'text-gray-600' };
    const deadlineTime = moment(deadline.toDate());
    const completedTime = moment(completedAt.toDate());
    const difference = completedTime.diff(deadlineTime, 'hours', true);
    if (difference <= 0) {
      return {
        status: `Early by ${Math.abs(difference).toFixed(1)}h`,
        class: 'text-green-600 font-semibold'
      };
    } else {
      return {
        status: `Late by ${difference.toFixed(1)}h`,
        class: 'text-red-600 font-semibold'
      };
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-500 via-emerald-500 to-emerald-600 py-4 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Page Header */}
        <div className="mb-4 text-center">
          <div className="flex items-center justify-center mb-2">
            <DocumentChartBarIcon className="h-10 w-10 text-white mr-3" />
            <h1 className="text-3xl font-black text-white">Task Completion Report</h1>
          </div>
          <p className="text-green-50 text-sm">Detailed analytics and metrics for completed tasks</p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-lg p-4 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2 rounded-xl border-2 border-gray-300 focus:border-green-500 focus:ring-2 focus:ring-green-200 transition-all text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-3 py-2 rounded-xl border-2 border-gray-300 focus:border-green-500 focus:ring-2 focus:ring-green-200 transition-all text-sm"
              />
            </div>
            <button
              onClick={() => {
                setStartDate('');
                setEndDate('');
              }}
              className="px-4 py-2 bg-gradient-to-r from-gray-600 to-gray-700 text-white font-bold rounded-xl hover:from-gray-700 hover:to-gray-800 transition-all text-sm"
            >
              Clear Filters
            </button>
          </div>
        </div>

        {/* Stats Summary */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <div className="bg-white rounded-xl shadow-lg p-4">
            <p className="text-xs font-bold text-gray-600 uppercase">Total Tasks</p>
            <p className="text-3xl font-black text-gray-800 mt-1">{filteredTasks.length}</p>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-4">
            <p className="text-xs font-bold text-gray-600 uppercase">Completed</p>
            <p className="text-3xl font-black text-green-600 mt-1">
              {filteredTasks.filter(t => t.completedAt).length}
            </p>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-4">
            <p className="text-xs font-bold text-gray-600 uppercase">On Time</p>
            <p className="text-3xl font-black text-emerald-600 mt-1">
              {filteredTasks.filter(t => {
                if (!t.deadline || !t.completedAt) return false;
                return moment(t.completedAt.toDate()).isSameOrBefore(moment(t.deadline.toDate()));
              }).length}
            </p>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-4">
            <p className="text-xs font-bold text-gray-600 uppercase">Late</p>
            <p className="text-3xl font-black text-red-600 mt-1">
              {filteredTasks.filter(t => {
                if (!t.deadline || !t.completedAt) return false;
                return moment(t.completedAt.toDate()).isAfter(moment(t.deadline.toDate()));
              }).length}
            </p>
          </div>
        </div>

        {/* Tasks Table */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gradient-to-r from-green-600 to-emerald-600 text-white">
                <tr>
                  <th className="px-2 py-1.5 text-left text-xs font-bold uppercase">Task Title</th>
                  <th className="px-2 py-1.5 text-left text-xs font-bold uppercase">Assigned To</th>
                  <th className="px-2 py-1.5 text-left text-xs font-bold uppercase">Start Date</th>
                  <th className="px-2 py-1.5 text-left text-xs font-bold uppercase">Deadline</th>
                  <th className="px-2 py-1.5 text-left text-xs font-bold uppercase">Completed</th>
                  <th className="px-2 py-1.5 text-left text-xs font-bold uppercase">Duration</th>
                  <th className="px-2 py-1.5 text-left text-xs font-bold uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredTasks.length > 0 ? (
                  filteredTasks.map((task) => {
                    const onTimeCheck = checkOnTime(task.deadline, task.completedAt);
                    return (
                      <tr key={task.id} className="hover:bg-gray-50">
                        <td className="px-2 py-1.5 text-xs font-medium text-gray-900">{task.taskTitle}</td>
                        <td className="px-2 py-1.5 text-xs text-gray-600">{task.assignedToName}</td>
                        <td className="px-2 py-1.5 text-xs text-gray-600">
                          {task.startDate ? moment(task.startDate.toDate()).format('MMM D, YYYY h:mm A') : 'N/A'}
                        </td>
                        <td className="px-2 py-1.5 text-xs text-gray-600">
                          {task.deadline ? moment(task.deadline.toDate()).format('MMM D, YYYY h:mm A') : 'N/A'}
                        </td>
                        <td className="px-2 py-1.5 text-xs text-gray-600">
                          {task.completedAt ? moment(task.completedAt.toDate()).format('MMM D, YYYY h:mm A') : (
                            <span className="px-2 py-1 bg-amber-100 text-amber-800 rounded-full text-xs font-semibold">
                              Pending
                            </span>
                          )}
                        </td>
                        <td className="px-2 py-1.5 text-xs text-gray-600">
                          {calculateTimeDifference(task.startDate, task.completedAt)}
                        </td>
                        <td className={`px-2 py-1.5 text-xs ${onTimeCheck.class}`}>
                          {onTimeCheck.status}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="7" className="px-2 py-8 text-center text-gray-500">
                      <CheckCircleIcon className="h-16 w-16 text-gray-300 mx-auto mb-3" />
                      <p className="text-sm">No tasks found for the selected date range</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskCompletionReport;
